import { storyblokEditable, StoryblokComponent } from "@storyblok/react/rsc";

import { GridContainer } from "@/components/layout/GridContainer";
import { cn } from "@/lib/utils";

type InfoSectionBackgroundColor = "sand-10" | "sand-100" | "sky-20" | "white" | "sky-50";

const backgroundColorClasses: Record<InfoSectionBackgroundColor, string> = {
  "sand-10": "bg-sand-10",
  "sand-100": "bg-sand-100",
  "sky-20": "bg-sky-20",
  "white": "bg-white",
  "sky-50": "bg-sky-50",
};

type InfoSectionColumns = "1" | "2";

type InfoSectionStoryblok = {
  subline?: string;
  title?: string;
  text?: string;
  background_color?: InfoSectionBackgroundColor;
  center_text?: boolean;
  columns?: InfoSectionColumns;
  body?: any[];
  _uid: string;
  component: "info_section";
  [k: string]: any;
};

type InfoSectionProps = {
  blok: InfoSectionStoryblok;
};

const InfoSection = ({ blok }: InfoSectionProps) => {
  const bgColor = blok.background_color as
    | InfoSectionBackgroundColor
    | undefined;
  const bgClass = bgColor ? backgroundColorClasses[bgColor] : undefined;
  const centered = blok.center_text;
  const twoColumns = blok.columns === "2";

  return (
    <section className={cn("py-12 rounded-3xl", bgClass)} {...storyblokEditable(blok)}>
      <GridContainer>
        <div className={cn(
          "col-span-12",
          twoColumns
            ? "md:col-span-12 lg:col-span-10 lg:col-start-2"
            : "md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3",
        )}>
          {/* Subline + Title */}
          {(blok.subline || blok.title) && (
            <div
              className={cn("flex flex-col gap-1", centered && "text-center")}
            >
              {blok.subline && (
                <p className="text-copy-bold ">{blok.subline}</p>
              )}
              {blok.title && (
                <h2 className="text-headline-xl ">{blok.title}</h2>
              )}
            </div>
          )}

          {/* Text */}
          {blok.text && (
            <p
              className={cn(
                "text-copy mt-6 whitespace-pre-line ",
                centered && "text-center",
              )}
            >
              {blok.text}
            </p>
          )}

          {/* Body: mix of info_list, info_text, etc. */}
          {blok.body && blok.body.length > 0 && (
            <div
              className={cn(
                "mt-6",
                twoColumns
                  ? "grid grid-cols-1 md:grid-cols-2 gap-6"
                  : "flex flex-col gap-6",
              )}
            >
              {blok.body.map((nestedBlok: any) => (
                <StoryblokComponent blok={nestedBlok} key={nestedBlok._uid} />
              ))}
            </div>
          )}
        </div>
      </GridContainer>
    </section>
  );
};

export default InfoSection;
